// PPS CRM - Settings Module
import { useState, useEffect } from 'react';
import { api } from '../lib/api';
import { useAuthStore } from '../stores/auth';
import logoDark from '../assets/brand/paradox-logo-transparent.png';

interface SystemSettings {
  companyName: string;
  companyEmail: string;
  companyPhone: string;
  address: string;
  timezone: string;
  dateFormat: string;
  currency: string;
  taxRate: number;
  commissionRate: number;
  commissionBasic: number;
  commissionStandard: number;
  commissionPremium: number;
  commissionMaximum: number;
  rushAddonFee: number;
}

export default function Settings() {
  const [settings, setSettings] = useState<SystemSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState<'general' | 'billing' | 'notifications' | 'security' | 'users'>('general');
  const { user } = useAuthStore();
  const [users, setUsers] = useState<any[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [createRole, setCreateRole] = useState<'AGENT' | 'MANAGER'>('AGENT');
  const [createForm, setCreateForm] = useState({
    email: '',
    fullLegalName: '',
    preferredName: '',
    phone: '',
    managerId: '',
  });
  const [lastTempPassword, setLastTempPassword] = useState<string | null>(null);

  useEffect(() => {
    fetchSettings();
  }, []);

  useEffect(() => {
    if (user?.role === 'OWNER' || user?.role === 'MANAGER') {
      setActiveTab('users');
      fetchUsers();
    }
  }, [user?.role]);

  const fetchSettings = async () => {
    try {
      const response = await api.get('/api/settings');
      setSettings(response.data);
    } catch (error) {
      console.error('Error fetching settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!settings) return;
    setSaving(true);
    try {
      await api.patch('/api/settings', settings);
      alert('Settings saved successfully!');
    } catch (error) {
      console.error('Error saving settings:', error);
    } finally {
      setSaving(false);
    }
  };

  const fetchUsers = async () => {
    setLoadingUsers(true);
    try {
      const response = await api.get('/api/users');
      setUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setLoadingUsers(false);
    }
  };

  const handleCreateUser = async () => {
    try {
      setLastTempPassword(null);
      if (createRole === 'AGENT') {
        const res = await api.post('/api/users/agent', createForm);
        setLastTempPassword(res.data.tempPassword || null);
      } else {
        const res = await api.post('/api/users/manager', createForm);
        setLastTempPassword(res.data.tempPassword || null);
      }
      setCreateForm({ email: '', fullLegalName: '', preferredName: '', phone: '', managerId: '' });
      fetchUsers();
    } catch (error) {
      console.error('Error creating user:', error);
    }
  };

  if (loading) {
    return <div className="p-8 text-center">Loading settings...</div>;
  }

  return (
    <div>
      <div className="settings-hero">
        <div className="settings-hero__brand">
          <img src={logoDark} alt="Paradox Payroll Systems" />
          <div>
            <div className="settings-hero__eyebrow">System</div>
            <div className="settings-hero__title">Settings</div>
          </div>
        </div>
        <button
          onClick={handleSave}
          disabled={saving}
          className="btn btn-primary"
        >
          {saving ? 'Saving...' : 'Save Changes'}
        </button>
      </div>

      {(user?.role === 'OWNER' || user?.role === 'MANAGER') && (
        <div className="card mb-6">
          <div className="flex items-center justify-between">
            <div>
              <div className="font-semibold">User Management</div>
              <div className="text-sm text-dark-gray">Create agents and managers from here.</div>
            </div>
            <button
              onClick={() => {
                setActiveTab('users');
                fetchUsers();
              }}
              className="bg-primary text-white px-4 py-2 rounded hover:bg-primary-dark"
            >
              Create User
            </button>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="card mb-6">
        <div className="flex border-b border-mid-gray">
          <button
            onClick={() => setActiveTab('general')}
            className={`px-4 py-3 font-medium ${
              activeTab === 'general' 
                ? 'border-b-2 border-primary text-primary' 
                : 'text-dark-gray'
            }`}
          >
            General
          </button>
          <button
            onClick={() => setActiveTab('billing')}
            className={`px-4 py-3 font-medium ${
              activeTab === 'billing' 
                ? 'border-b-2 border-primary text-primary' 
                : 'text-dark-gray'
            }`}
          >
            Billing & Commission
          </button>
          <button
            onClick={() => setActiveTab('notifications')}
            className={`px-4 py-3 font-medium ${
              activeTab === 'notifications' 
                ? 'border-b-2 border-primary text-primary' 
                : 'text-dark-gray'
            }`}
          >
            Notifications
          </button>
          <button
            onClick={() => setActiveTab('security')}
            className={`px-4 py-3 font-medium ${
              activeTab === 'security' 
                ? 'border-b-2 border-primary text-primary' 
                : 'text-dark-gray'
            }`}
          >
            Security
          </button>
          {(user?.role === 'OWNER' || user?.role === 'MANAGER') && (
            <button
              onClick={() => {
                setActiveTab('users');
                fetchUsers();
              }}
              className={`px-4 py-3 font-medium ${
                activeTab === 'users' 
                  ? 'border-b-2 border-primary text-primary' 
                  : 'text-dark-gray'
              }`}
            >
              Users
            </button>
          )}
        </div>
      </div>

      {/* General Settings */}
      {activeTab === 'general' && settings && (
        <div className="card">
          <h3 className="font-semibold mb-4">Company Information</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Company Name</label>
              <input
                type="text"
                value={settings.companyName}
                onChange={(e) => setSettings({ ...settings, companyName: e.target.value })}
                className="w-full px-3 py-2 border border-mid-gray rounded"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Email</label>
              <input
                type="email"
                value={settings.companyEmail}
                onChange={(e) => setSettings({ ...settings, companyEmail: e.target.value })}
                className="w-full px-3 py-2 border border-mid-gray rounded"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Phone</label>
              <input
                type="tel"
                value={settings.companyPhone}
                onChange={(e) => setSettings({ ...settings, companyPhone: e.target.value })}
                className="w-full px-3 py-2 border border-mid-gray rounded"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Timezone</label>
              <select
                value={settings.timezone}
                onChange={(e) => setSettings({ ...settings, timezone: e.target.value })}
                className="w-full px-3 py-2 border border-mid-gray rounded"
              >
                <option value="America/New_York">Eastern Time</option>
                <option value="America/Chicago">Central Time</option>
                <option value="America/Denver">Mountain Time</option>
                <option value="America/Los_Angeles">Pacific Time</option>
              </select>
            </div>
          </div>

          <div className="mt-4">
            <label className="block text-sm font-medium mb-1">Address</label>
            <textarea
              value={settings.address}
              onChange={(e) => setSettings({ ...settings, address: e.target.value })}
              rows={2}
              className="w-full px-3 py-2 border border-mid-gray rounded"
            />
          </div>

          <div className="grid grid-cols-2 gap-4 mt-4">
            <div>
              <label className="block text-sm font-medium mb-1">Date Format</label>
              <select
                value={settings.dateFormat}
                onChange={(e) => setSettings({ ...settings, dateFormat: e.target.value })}
                className="w-full px-3 py-2 border border-mid-gray rounded"
              >
                <option value="MM/DD/YYYY">MM/DD/YYYY</option>
                <option value="DD/MM/YYYY">DD/MM/YYYY</option>
                <option value="YYYY-MM-DD">YYYY-MM-DD</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Currency</label>
              <select
                value={settings.currency}
                onChange={(e) => setSettings({ ...settings, currency: e.target.value })}
                className="w-full px-3 py-2 border border-mid-gray rounded"
              >
                <option value="USD">USD ($)</option>
                <option value="EUR">EUR (€)</option>
                <option value="GBP">GBP (£)</option>
              </select>
            </div>
          </div>
        </div>
      )}

      {/* Billing Settings */}
      {activeTab === 'billing' && settings && (
        <div className="card">
          <h3 className="font-semibold mb-4">Billing & Commission Settings</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Default Tax Rate (%)</label>
              <input
                type="number"
                min={0}
                max={100}
                step={0.01}
                value={settings.taxRate}
                onChange={(e) => setSettings({ ...settings, taxRate: Number(e.target.value) })}
                className="w-full px-3 py-2 border border-mid-gray rounded"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Default Commission Rate (%)</label>
              <input
                type="number"
                min={0}
                max={100}
                step={0.01}
                value={settings.commissionRate}
                onChange={(e) => setSettings({ ...settings, commissionRate: Number(e.target.value) })}
                className="w-full px-3 py-2 border border-mid-gray rounded"
              />
            </div>
          </div>

          <div className="mt-6">
            <div className="font-semibold mb-2">Flat-Rate Commissions Per Package</div>
            <div className="text-sm text-dark-gray mb-4">These are the fixed commission amounts paid per package.</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Basic Package</label>
                <input
                  type="number"
                  min={0}
                  step={0.01}
                  value={settings.commissionBasic}
                  onChange={(e) => setSettings({ ...settings, commissionBasic: Number(e.target.value) })}
                  className="w-full px-3 py-2 border border-mid-gray rounded"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Standard Package</label>
                <input
                  type="number"
                  min={0}
                  step={0.01}
                  value={settings.commissionStandard}
                  onChange={(e) => setSettings({ ...settings, commissionStandard: Number(e.target.value) })}
                  className="w-full px-3 py-2 border border-mid-gray rounded"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Premium Package</label>
                <input
                  type="number"
                  min={0}
                  step={0.01}
                  value={settings.commissionPremium}
                  onChange={(e) => setSettings({ ...settings, commissionPremium: Number(e.target.value) })}
                  className="w-full px-3 py-2 border border-mid-gray rounded"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Maximum Package</label>
                <input
                  type="number"
                  min={0}
                  step={0.01}
                  value={settings.commissionMaximum}
                  onChange={(e) => setSettings({ ...settings, commissionMaximum: Number(e.target.value) })}
                  className="w-full px-3 py-2 border border-mid-gray rounded"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Rush Add-on (Any Package)</label>
                <input
                  type="number"
                  min={0}
                  step={0.01}
                  value={settings.rushAddonFee}
                  onChange={(e) => setSettings({ ...settings, rushAddonFee: Number(e.target.value) })}
                  className="w-full px-3 py-2 border border-mid-gray rounded"
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Notifications Settings */}
      {activeTab === 'notifications' && (
        <div className="card">
          <h3 className="font-semibold mb-4">Notification Preferences</h3>
          <div className="space-y-4">
            {[
              { id: 'email_new_client', label: 'New client assigned', description: 'Email me when a new client is assigned' },
              { id: 'email_task_reminder', label: 'Task reminders', description: 'Email me about upcoming task deadlines' },
              { id: 'email_commission', label: 'Commission updates', description: 'Email me when commissions are approved or paid' },
              { id: 'push_messages', label: 'New messages', description: 'Push notifications for new messages' },
              { id: 'push_leads', label: 'New leads', description: 'Push notifications when new leads are assigned' },
            ].map((item) => (
              <div key={item.id} className="flex items-center justify-between p-4 bg-light-gray rounded">
                <div>
                  <div className="font-medium">{item.label}</div>
                  <div className="text-sm text-dark-gray">{item.description}</div>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input type="checkbox" className="sr-only peer" defaultChecked />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                </label>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Security Settings */}
      {activeTab === 'security' && (
        <div className="card">
          <h3 className="font-semibold mb-4">Security Settings</h3>
          <div className="space-y-4">
            <div className="p-4 bg-light-gray rounded">
              <h4 className="font-medium mb-2">Change Password</h4>
              <div className="space-y-3">
                <input
                  type="password"
                  placeholder="Current password"
                  className="w-full px-3 py-2 border border-mid-gray rounded"
                />
                <input
                  type="password"
                  placeholder="New password"
                  className="w-full px-3 py-2 border border-mid-gray rounded"
                />
                <input
                  type="password"
                  placeholder="Confirm new password"
                  className="w-full px-3 py-2 border border-mid-gray rounded"
                />
                <button className="bg-primary text-white px-4 py-2 rounded hover:bg-primary-dark">
                  Update Password
                </button>
              </div>
            </div>

            <div className="p-4 bg-light-gray rounded">
              <h4 className="font-medium mb-2">Two-Factor Authentication</h4>
              <p className="text-sm text-dark-gray mb-3">
                Add an extra layer of security to your account
              </p>
              <button className="text-primary hover:underline">
                Enable 2FA →
              </button>
            </div>

            <div className="p-4 bg-light-gray rounded">
              <h4 className="font-medium mb-2">Active Sessions</h4>
              <p className="text-sm text-dark-gray mb-3">
                Manage devices where you're currently logged in
              </p>
              <button className="text-primary hover:underline">
                View Sessions →
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Users */}
      {activeTab === 'users' && (
        <div className="card">
          <h3 className="font-semibold mb-4">User Management</h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <div>
              <label className="block text-sm font-medium mb-1">Role</label>
              <select
                value={createRole}
                onChange={(e) => setCreateRole(e.target.value as any)}
                className="w-full px-3 py-2 border border-mid-gray rounded"
              >
                <option value="AGENT">Agent</option>
                {user?.role === 'OWNER' && <option value="MANAGER">Manager</option>}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Email</label>
              <input
                type="email"
                value={createForm.email}
                onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })}
                className="w-full px-3 py-2 border border-mid-gray rounded"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Full Legal Name</label>
              <input
                type="text"
                value={createForm.fullLegalName}
                onChange={(e) => setCreateForm({ ...createForm, fullLegalName: e.target.value })}
                className="w-full px-3 py-2 border border-mid-gray rounded"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Preferred Name</label>
              <input
                type="text"
                value={createForm.preferredName}
                onChange={(e) => setCreateForm({ ...createForm, preferredName: e.target.value })}
                className="w-full px-3 py-2 border border-mid-gray rounded"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Phone</label>
              <input
                type="tel"
                value={createForm.phone}
                onChange={(e) => setCreateForm({ ...createForm, phone: e.target.value })}
                className="w-full px-3 py-2 border border-mid-gray rounded"
              />
            </div>
            {createRole === 'AGENT' && user?.role === 'OWNER' && (
              <div>
                <label className="block text-sm font-medium mb-1">Manager ID (optional)</label>
                <input
                  type="text"
                  value={createForm.managerId}
                  onChange={(e) => setCreateForm({ ...createForm, managerId: e.target.value })}
                  className="w-full px-3 py-2 border border-mid-gray rounded"
                />
              </div>
            )}
          </div>

          <button
            onClick={handleCreateUser}
            className="bg-primary text-white px-4 py-2 rounded hover:bg-primary-dark"
          >
            Create User
          </button>

          {lastTempPassword && (
            <div className="mt-4 p-3 rounded bg-light-gray">
              Temporary password: <strong>{lastTempPassword}</strong>
            </div>
          )}

          <div className="mt-6">
            <h4 className="font-medium mb-2">Users</h4>
            {loadingUsers ? (
              <div className="p-4 text-center">Loading users...</div>
            ) : users.length === 0 ? (
              <div className="p-4 text-center text-dark-gray">No users found</div>
            ) : (
              <table className="w-full">
                <thead className="bg-light-gray">
                  <tr>
                    <th className="px-4 py-3 text-left">Name</th>
                    <th className="px-4 py-3 text-left">Email</th>
                    <th className="px-4 py-3 text-left">Role</th>
                    <th className="px-4 py-3 text-left">Status</th>
                    <th className="px-4 py-3 text-left">Agent Code</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((u) => (
                    <tr key={u.id} className="border-b border-light-gray hover:bg-light-gray">
                      <td className="px-4 py-3">{u.fullLegalName}</td>
                      <td className="px-4 py-3">{u.email}</td>
                      <td className="px-4 py-3">{u.role}</td>
                      <td className="px-4 py-3">{u.status}</td>
                      <td className="px-4 py-3">{u.agentCode || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
